import { useState, useRef, useCallback, useEffect } from "react";
import { INTERNAL_POST_MESSAGE_EVENT_NAMES } from "../constants";
import { getOrigin } from "../utilities";

const { LOGIN_EVENT, SKIP_LOGIN, INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT } =
  INTERNAL_POST_MESSAGE_EVENT_NAMES;

// messages to be sent whenever a gameplay iframe mounts (or gameplay phase changes)
// determine which msgs to send based on whether the app is embedded
// if not embedded, add skip login msg, otherwise add init input
// dispatching msg (which will enable tracking the eventual logged-in user's input)
const MESSAGES_TO_SEND_ON_GAMEPLAY_MOUNT = [
  window === window.parent
    ? { eventName: SKIP_LOGIN }
    : {
        eventName: INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT,
        inputTypes: ["click", "keydown"],
        options: { dispatchPuzzleMoveCtrlOnly: true },
      },
];

const useGameplayMessaging = (gameRef, playtimePhase = null) => {
  const [isGameplayMessagingReady, setIsGameplayMessagingReady] =
    useState(false);
  // login data used to login every time a gameplay iframe is rendered
  const [gameplayLoginData, setGameplayLoginData] = useState(null);
  // acts as the flag that triggers message sending useEffect
  const [isMsgQueueEmpty, setIsMsgQueueEmpty] = useState(true);
  const [initializedPlaytimePhase, setInitializedPlaytimePhase] =
    useState(null);
  const msgQueueRef = useRef([]);
  const iframeContentWindow = gameRef?.current?.contentWindow;

  const addGameplayMsgToQueue = useCallback((eventName, otherData = {}) => {
    msgQueueRef.current.push({
      ...otherData,
      eventName,
    });
    setIsMsgQueueEmpty(false);
  }, []);

  // message send handling
  useEffect(() => {
    if (
      isGameplayMessagingReady &&
      iframeContentWindow &&
      // ultra safe check to ensure things get synced in case they get out of sync
      (!isMsgQueueEmpty || msgQueueRef.current.length) &&
      // ensures old msgs from previous playtime phase don't get sent if phase was updated
      initializedPlaytimePhase === playtimePhase
    ) {
      while (msgQueueRef.current.length) {
        iframeContentWindow.postMessage(
          JSON.stringify(msgQueueRef.current.shift()),
          getOrigin("play")
        );
      }
      setIsMsgQueueEmpty(true);
    }
  }, [
    isGameplayMessagingReady,
    isMsgQueueEmpty,
    iframeContentWindow,
    initializedPlaytimePhase,
    playtimePhase,
  ]);

  // gameplay phase change handling
  useEffect(() => {
    if (initializedPlaytimePhase !== playtimePhase) {
      // need to reset (if it was set) for next time gameplay is rendered
      setIsGameplayMessagingReady(false);
      // clear queue
      msgQueueRef.current = [];
      setIsMsgQueueEmpty(true);
      // add initial msgs for new playtime phase gameplay iframe to receive
      if (playtimePhase) {
        MESSAGES_TO_SEND_ON_GAMEPLAY_MOUNT.forEach((msg) => {
          const { eventName, ...otherData } = msg;
          addGameplayMsgToQueue(eventName, otherData);
        });
      }
      // mark current playtime phase as initialized
      setInitializedPlaytimePhase(playtimePhase);
    }
  }, [initializedPlaytimePhase, playtimePhase, addGameplayMsgToQueue]);

  // login msg enqueue handling
  useEffect(() => {
    if (initializedPlaytimePhase === playtimePhase && gameplayLoginData) {
      addGameplayMsgToQueue(LOGIN_EVENT, gameplayLoginData);
    }
  }, [
    initializedPlaytimePhase,
    playtimePhase,
    gameplayLoginData,
    addGameplayMsgToQueue,
  ]);

  return [
    setIsGameplayMessagingReady,
    setGameplayLoginData,
    addGameplayMsgToQueue,
  ];
};

export default useGameplayMessaging;
